import React from "react";
import { BadgeCheck, ClipboardList, Clock, Mail, MapPin, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { useBookingModal } from "../context/BookingModalContext";
import { OFFICE_ADDRESS, OFFICE_MAP_URL } from "../config";

const contactDetails = [
  {
    icon: Phone,
    labelKey: "contact.labels.phone",
    value: "+91 88660 77880 / +91 94265 87801",
    href: "tel:+918866077880",
    external: false,
  },
  {
    icon: Mail,
    labelKey: "contact.labels.email",
    value: "support@shreemateltourism.in",
    href: "mailto:support@shreemateltourism.in",
    external: false,
  },
  {
    icon: MapPin,
    labelKey: "contact.labels.address",
    value: OFFICE_ADDRESS,
    href: OFFICE_MAP_URL,
    external: true,
  },
  {
    icon: Clock,
    labelKey: "contact.labels.workingHours",
    value: null,
    href: null,
    external: false,
  },
];

const highlightKeys = ["trusted", "bestPrices", "support", "custom"];

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const { open } = useBookingModal();

  useDocumentMeta({
    title: t("contact.metaTitle"),
    description: t("contact.metaDescription"),
    path: "/contact",
  });

  return (
    <div className="bg-sand-50 min-h-screen">
      {/* Header */}
      <div className="relative bg-navy-950 py-24 text-white text-center overflow-hidden">
        <div className="absolute -bottom-20 right-10 w-80 h-80 rounded-full bg-brand-500/10 blur-3xl" />
        <div className="relative">
          <span className="inline-block border border-brand-400/40 bg-brand-500/10 text-brand-300 text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-[0.2em]">
            {t("contact.eyebrow")}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold mt-2">{t("contact.title")}</h1>
          <div className="w-16 h-[3px] bg-gradient-to-r from-brand-600 to-brand-400 mx-auto mt-6 rounded-full" />
          <p className="text-navy-200 mt-6 text-lg max-w-xl mx-auto px-6">
            {t("contact.subtitle")}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* Contact Info */}
        <div className="space-y-5">
          <h2 className="font-serif text-2xl font-bold text-navy-900">{t("contact.ourDetails")}</h2>
          {contactDetails.map(({ icon: Icon, labelKey, value, href, external }) => (
            <div
              key={labelKey}
              className="flex items-start gap-4 p-5 bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-all border border-navy-900/5"
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-brand-600 to-brand-400 text-navy-950 shrink-0">
                <Icon size={20} strokeWidth={2} />
              </span>
              <div>
                <p className="text-[11px] text-navy-900/40 font-semibold uppercase tracking-wider mb-0.5">
                  {t(labelKey)}
                </p>
                {href ? (
                  <a
                    href={href}
                    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="text-navy-900 font-semibold hover:text-brand-700 transition-colors break-words"
                  >
                    {value}
                  </a>
                ) : (
                  <p className="text-navy-900 font-semibold">
                    {value ?? t("contact.workingHoursValue")}
                  </p>
                )}
              </div>
            </div>
          ))}

          <a
            href="tel:+918866077880"
            className="flex items-center justify-center gap-3 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-bold px-6 py-4 rounded-2xl hover:shadow-lg hover:-translate-y-0.5 transition-all"
          >
            <Phone size={22} />
            <div className="text-left">
              <div>{t("contact.callUsNow")}</div>
              <div className="text-xs font-normal text-emerald-100">
                +91 88660 77880 / +91 94265 87801
              </div>
            </div>
          </a>
        </div>

        {/* Quick Inquiry */}
        <div className="bg-white rounded-2xl shadow-card p-8 border border-navy-900/5 h-fit">
          <h2 className="font-serif text-2xl font-bold text-navy-900 mb-6">{t("contact.quickInquiry")}</h2>
          <button
            onClick={() => open({ title: t("contact.generalInquiryTitle") })}
            className="w-full bg-gradient-to-r from-brand-600 to-brand-400 text-navy-950 font-bold py-4 rounded-2xl shadow-gold hover:-translate-y-0.5 hover:shadow-xl transition-all text-lg flex items-center justify-center gap-3"
          >
            <ClipboardList size={22} />
            {t("contact.openForm")}
          </button>
          <p className="text-navy-900/45 text-sm text-center mt-4">
            {t("contact.formHint")}
          </p>

          <div className="mt-8 p-5 bg-sand-100 rounded-2xl border border-brand-200">
            <p className="text-sm text-navy-900 font-bold mb-3">{t("contact.whyChooseUs")}</p>
            <ul className="space-y-2.5 text-sm text-navy-900/60">
              {highlightKeys.map((h) => (
                <li key={h} className="flex items-center gap-2.5">
                  <BadgeCheck size={16} className="text-brand-600 shrink-0" />
                  {t(`contact.highlights.${h}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
